import { Button, Stack } from '@mui/joy';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { approvedVendorService } from 'companySettings/services';
import { AgGridServerTable, PageTitle } from 'core/components';
import { SortDirection } from 'core/enums';
import { AgGridServerTableProvider, SelectionCountProvider } from 'core/states';
import { AgGridGetRowsParams, ITableActionProps } from 'core/types';
import { Color } from 'theme/enums';

import { AddVendorAssociation } from './AddVendorAssociation';
import { getColumnDef } from './columns';

export const ApprovedVendors = () => {
  const { t } = useTranslation();

  const columnDefs = useMemo(() => getColumnDef(), []);

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) =>
      approvedVendorService.getApprovedVendors({
        filters,
        sortField: 'manufacturerName',
        sortDir: SortDirection.ASC,
        ...rest,
      }),
    []
  );

  // Refetched rather than added through `updateRows`: one submit creates a row per picked vendor
  // location, and the Manufacturer column's `rowSpan` is computed across the whole node set, so a
  // transactional add would leave every sibling row's span stale. Same call the in-cell add uses.
  const actionButtons = useCallback(
    ({ gridApi }: ITableActionProps) => [
      <AddVendorAssociation
        key="addVendorAssociationButton"
        onSubmitSuccess={() => gridApi?.refreshServerSide({ purge: true })}
        requireDefaultVendor
        button={<Button color={Color.SUCCESS}>+ {t('buttonLabelAddManufacturer')}</Button>}
      />,
    ],
    [t]
  );

  return (
    <Stack sx={{ gap: 2, height: '100%' }}>
      <PageTitle title={t('titleApprovedVendors')} />

      <SelectionCountProvider>
        <AgGridServerTableProvider
          tableId="approvedVendorsTable"
          entityType="ApprovedVendor"
          columnDefs={columnDefs}
          getRows={getRows}
          suppressRowTransform
          suppressCellFocus
          hasRowSelection={false}
        >
          <AgGridServerTable actionButtons={actionButtons} />
        </AgGridServerTableProvider>
      </SelectionCountProvider>
    </Stack>
  );
};
